/**
 * Custom Modal Utility - Replaces browser alert() with selectable text
 */

/**
 * Show a modal dialog with selectable text
 * @param {string} message - The message to display (supports line breaks)
 * @param {string} type - Type of modal: 'success', 'error', 'info', 'warning'
 * @param {string} title - Optional title for the modal
 */
export function showModal(message, type = 'info', title = null) {
  return new Promise((resolve) => {
    // Remove any existing modals
    const existingModal = document.querySelector('.custom-modal-overlay');
    if (existingModal) {
      existingModal.remove();
    }

    // Determine icon and title based on type
    const icons = {
      success: '✅',
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️',
    };

    const titles = {
      success: 'Success',
      error: 'Error',
      warning: 'Warning',
      info: 'Information',
    };

    const icon = icons[type] || icons.info;
    const modalTitle = title || titles[type] || titles.info;

    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.className = 'custom-modal-overlay modal-overlay';
    overlay.style.userSelect = 'text'; // Allow text selection
    overlay.style.webkitUserSelect = 'text'; // Safari
    overlay.style.cursor = 'default';

    // Create modal content
    overlay.innerHTML = `
      <div class="modal" style="max-width: 500px; user-select: text; -webkit-user-select: text;">
        <div class="modal-header">
          <h3 style="margin: 0; display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 24px;">${icon}</span>
            <span>${modalTitle}</span>
          </h3>
          <button class="modal-close custom-modal-close" aria-label="Close">×</button>
        </div>
        <div class="modal-body" style="user-select: text; -webkit-user-select: text; white-space: pre-wrap; line-height: 1.6; margin: 20px 0; color: var(--color-mist-grey);">
          ${message.replace(/\n/g, '<br>')}
        </div>
        <div style="display: flex; justify-content: flex-end; gap: 12px;">
          <button class="btn btn-primary custom-modal-ok">OK</button>
        </div>
      </div>
    `;

    // Add to DOM
    document.body.appendChild(overlay);

    // Handle close actions
    const closeModal = () => {
      overlay.remove();
      resolve();
    };

    overlay.querySelector('.custom-modal-close').addEventListener('click', closeModal);
    overlay.querySelector('.custom-modal-ok').addEventListener('click', closeModal);

    // Close on overlay click (outside modal)
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closeModal();
      }
    });

    // Close on Escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);

    // Focus OK button
    setTimeout(() => {
      overlay.querySelector('.custom-modal-ok').focus();
    }, 100);
  });
}

/**
 * Show a confirmation dialog
 * @param {string} message - The message to display
 * @param {string} title - Optional title
 */
export function showConfirm(message, title = 'Confirm') {
  return new Promise((resolve) => {
    // Remove any existing modals
    const existingModal = document.querySelector('.custom-modal-overlay');
    if (existingModal) {
      existingModal.remove();
    }

    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.className = 'custom-modal-overlay modal-overlay';
    overlay.style.userSelect = 'text';
    overlay.style.webkitUserSelect = 'text';

    // Create modal content
    overlay.innerHTML = `
      <div class="modal" style="max-width: 500px; user-select: text; -webkit-user-select: text;">
        <div class="modal-header">
          <h3 style="margin: 0; display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 24px;">❓</span>
            <span>${title}</span>
          </h3>
          <button class="modal-close custom-modal-close" aria-label="Close">×</button>
        </div>
        <div class="modal-body" style="user-select: text; -webkit-user-select: text; white-space: pre-wrap; line-height: 1.6; margin: 20px 0; color: var(--color-mist-grey);">
          ${message.replace(/\n/g, '<br>')}
        </div>
        <div style="display: flex; justify-content: flex-end; gap: 12px;">
          <button class="btn btn-secondary custom-modal-cancel">Cancel</button>
          <button class="btn btn-primary custom-modal-confirm">OK</button>
        </div>
      </div>
    `;

    // Add to DOM
    document.body.appendChild(overlay);

    // Handle actions
    const closeModal = (result) => {
      overlay.remove();
      resolve(result);
    };

    overlay.querySelector('.custom-modal-close').addEventListener('click', () => closeModal(false));
    overlay.querySelector('.custom-modal-cancel').addEventListener('click', () => closeModal(false));
    overlay.querySelector('.custom-modal-confirm').addEventListener('click', () => closeModal(true));

    // Close on overlay click (outside modal)
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closeModal(false);
      }
    });

    // Close on Escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeModal(false);
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);

    // Focus confirm button
    setTimeout(() => {
      overlay.querySelector('.custom-modal-confirm').focus();
    }, 100);
  });
}

/**
 * Show a prompt dialog
 * @param {string} message - The message to display
 * @param {string} defaultValue - Default input value
 * @param {string} title - Optional title
 */
export function showPrompt(message, defaultValue = '', title = 'Input') {
  return new Promise((resolve) => {
    // Remove any existing modals
    const existingModal = document.querySelector('.custom-modal-overlay');
    if (existingModal) {
      existingModal.remove();
    }

    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.className = 'custom-modal-overlay modal-overlay';

    // Create modal content
    overlay.innerHTML = `
      <div class="modal" style="max-width: 500px;">
        <div class="modal-header">
          <h3 style="margin: 0;">${title}</h3>
          <button class="modal-close custom-modal-close" aria-label="Close">×</button>
        </div>
        <div class="modal-body" style="margin: 20px 0;">
          <p style="margin-bottom: 12px; color: var(--color-mist-grey);">${message}</p>
          <input type="text" class="form-input custom-modal-input" value="${defaultValue}" style="width: 100%;">
        </div>
        <div style="display: flex; justify-content: flex-end; gap: 12px;">
          <button class="btn btn-secondary custom-modal-cancel">Cancel</button>
          <button class="btn btn-primary custom-modal-confirm">OK</button>
        </div>
      </div>
    `;

    // Add to DOM
    document.body.appendChild(overlay);

    const input = overlay.querySelector('.custom-modal-input');

    // Handle actions
    const closeModal = (result) => {
      overlay.remove();
      resolve(result);
    };

    const handleConfirm = () => closeModal(input.value);

    overlay.querySelector('.custom-modal-close').addEventListener('click', () => closeModal(null));
    overlay.querySelector('.custom-modal-cancel').addEventListener('click', () => closeModal(null));
    overlay.querySelector('.custom-modal-confirm').addEventListener('click', handleConfirm);

    // Submit on Enter
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        handleConfirm();
      }
    });

    // Close on Escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeModal(null);
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);

    // Focus input
    setTimeout(() => {
      input.focus();
      input.select();
    }, 100);
  });
}
